import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';
import { TransformWrapper } from 'react-zoom-pan-pinch';
import { useExpandedFdocContentContext } from '../ExpandedFdocContentContext.js';
import HeaderControls from './HeaderControls.js';
import PDFContentProvider from './PDF.js';
import PDFToolbar from './PDFToolbar.js';
import ScalableContainer from './ScalableContainer.js';
import TinyPagesList from './TinyPagesList.js';
import { useResourceDocumentDataContextSafe } from '../../context/resourceDataContext.js';
import { useExpandedFdocContext } from '../ExpandedFdocProvider.js';
import { PdfDocument } from './PDFDocument.js';
import { styled } from 'styled-components';
import { useQueryUserInfo } from '../../../../user/queries/useQueryUserInfo.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { hasMacOSKbd } from '@fabric-app/utils/detection';

const ContentPDF = styled.div.withConfig({
  displayName: "StoredPDFContent__ContentPDF",
  componentId: "sc-3018bf69-0"
})([
  `display:flex;position:relative;transition:transform 0.5s ease-in-out;height:100%;overflow:hidden;&[pan-mode='true']{cursor:grab;*{cursor:grab;}&[data-panning='true']{cursor:grabbing;user-select:none;*{cursor:grabbing;user-select:none;}}&[data-zooming='true']{cursor:zoom-in;*{cursor:zoom-in;}}}&::-webkit-scrollbar{width:0.5rem;height:0.5rem;}&::-webkit-scrollbar-thumb{background:rgb(146 146 146 / 30%);border-radius:1.5rem;visibility:hidden;}&:hover::-webkit-scrollbar-thumb{visibility:visible;}&::-webkit-scrollbar-thumb:active,&::-webkit-scrollbar-thumb:hover{background-color:rgb(146 146 146 / 40%);visibility:visible;}&::-webkit-scrollbar-track{background:rgb(0 0 0 / 7%);border-radius:1.5rem;visibility:hidden;}&:hover::-webkit-scrollbar-track{visibility:visible;}flex:1;`
]);
const PDFInner = styled.div.withConfig({
  displayName: "StoredPDFContent__PDFInner",
  componentId: "sc-3018bf69-1"
})([
  `display:flex;flex-grow:1;max-height:100%;.transform-wrapper{max-height:100%;}`
]);
const DocumentLoader = styled.div.withConfig({
  displayName: "StoredPDFContent__DocumentLoader",
  componentId: "sc-3018bf69-2"
})([
  `position:absolute;inset:0;background:white;z-index:10;display:flex;justify-content:center;align-items:center;`
]);
const ErrorDisplay = styled.div.withConfig({
  displayName: "StoredPDFContent__ErrorDisplay",
  componentId: "sc-3018bf69-3"
})([
  `display:flex;flex-direction:column;gap:16px;justify-content:center;align-items:center;height:100%;width:100%;font-size:14px;font-weight:600;letter-spacing:-0.025em;text-align:center;background:white;> button{text-decoration:underline;}:last-child{margin-bottom:10px;}`
]);
const TINY_PAGE_WIDTH = 46;
const TINY_PAGE_WIDTH_FULL_SCREEN = 79;
const StoredPDFContent = ({ fileUrl: fileUrlProp, isFullScreen, tinyPagesIsVisible }) => {
  const { resource, fullName } = useResourceDocumentDataContextSafe();
  const [currentPage, setCurrentPage] = useState(1);
  const { extraZoneRef } = useExpandedFdocContentContext();
  const { pdf, setPdf } = useExpandedFdocContext();
  const contentRef = useRef(null);
  const [contentRect] = useDomContentRect(contentRef.current);
  const [innerRef, setInnerRef] = useState(null);
  const [userScrolled, setUserScrolled] = useState(false);
  const { data: user } = useQueryUserInfo();
  const [documentLoading, setDocumentLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const fileUrl = fileUrlProp || resource.fileUrl;
  const onTryAgain = (e) => {
    e?.preventDefault();
    setDocumentLoading(true);
    setRetryAttempts(0);
    setError(null);
  };
  const onLoadError = (error2) => {
    setRetryAttempts((attempts) => attempts + 1);
    if (retryAttempts > 3) {
      setError(error2);
      setDocumentLoading(false);
      return;
    }
  };
  const onLoadSuccess = (nextPDF) => {
    setDocumentLoading(false);
    setPdf(nextPDF);
  };
  const [hasSelection, setHasSelection] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  useEffect(() => {
    if (!contentRef.current) return;
    const onSelectionChange = (_) => {
      if (!contentRef.current) return;
      const selection = window.getSelection();
      if (!selection) {
        setHasSelection(false);
        return;
      }
      if (!contentRef.current.contains(selection.anchorNode)) {
        setHasSelection(false);
        return;
      }
      if (selection.type !== "Range") {
        setHasSelection(false);
        return;
      }
      setHasSelection(true);
    };
    document.addEventListener("selectionchange", onSelectionChange, true);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange, true);
    };
  }, [
    contentRef
  ]);
  useEffect(() => {
    if (!contentRef.current) return;
    let mouseDown = false;
    let lastElement = null;
    const onMouseDown = (_) => {
      setIsMouseDown(true);
      mouseDown = true;
    };
    const onMouseUp = (_) => {
      setIsMouseDown(false);
      mouseDown = false;
      if (lastElement) lastElement.dataset["current"] = "false";
    };
    const onMouseMove = (e) => {
      if (!mouseDown) return;
      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (!element) return;
      if (lastElement) lastElement.dataset["current"] = "false";
      lastElement = element;
      lastElement.dataset["current"] = "true";
    };
    window.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("mouseup", onMouseUp, true);
    window.addEventListener("mousemove", onMouseMove, true);
    return () => {
      window.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("mouseup", onMouseUp, true);
      window.removeEventListener("mousemove", onMouseMove, true);
    };
  }, [
    contentRef
  ]);
  const [isModDown, setIsModDown] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (hasMacOSKbd() && e.key !== "Meta" || !hasMacOSKbd() && e.key !== "Control") return;
      setIsModDown(true);
    };
    const onKeyUp = (e) => {
      if (hasMacOSKbd() && e.key !== "Meta" || !hasMacOSKbd() && e.key !== "Control") return;
      setIsModDown(false);
    };
    const onBlur = () => {
      setIsModDown(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);
  const [zoomRef, setZoomRef] = useState(null);
  const [scale, setScale] = useState(1);
  const [positionY, setPositionY] = useState(0);
  useEffect(() => {
    if (!zoomRef) return;
    const wrapperComponent = zoomRef.instance.wrapperComponent;
    const currentDocument = wrapperComponent?.ownerDocument;
    const currentWindow = currentDocument?.defaultView;
    const onWheel = (e) => {
      if (!wrapperComponent?.contains(e.target)) return;
      setUserScrolled(true);
    };
    currentWindow?.addEventListener("wheel", onWheel, true);
    return () => {
      currentWindow?.removeEventListener("wheel", onWheel, true);
    };
  }, [
    zoomRef
  ]);
  return /* @__PURE__ */ jsx(TransformWrapper, {
    onInit: (ref) => setZoomRef(ref),
    initialScale: 1,
    limitToBounds: true,
    disablePadding: true,
    onZoomStop: (ref) => setScale(ref.state.scale),
    onTransformed: (_, state) => {
      setScale(state.scale);
      setPositionY(state.positionY);
    },
    onPanningStart: () => {
      setIsPanning(true);
    },
    onPanningStop: () => {
      setUserScrolled(true);
      setIsPanning(false);
    },
    onWheelStop: () => {
      setUserScrolled(true);
    },
    onPinchingStop: () => {
      setUserScrolled(true);
    },
    wheel: {
      wheelDisabled: !isModDown
    },
    panning: {
      wheelPanning: !isModDown,
      allowLeftClickPan: isModDown,
      allowRightClickPan: isModDown,
      allowMiddleClickPan: isModDown
    },
    doubleClick: {
      disabled: true
    },
    minScale: isFullScreen ? 0.2 : 1,
    maxScale: isFullScreen ? 3 : 1,
    children: /* @__PURE__ */ jsx(ContentPDF, {
      "data-pan-id-ignore-resource-drag-resize": "xy",
      "data-panning": isPanning,
      "data-pan-mode": isPanning || isModDown,
      ref: contentRef,
      children: error ? /* @__PURE__ */ jsxs(ErrorDisplay, {
        children: [
          /* @__PURE__ */ jsx("h6", {
            children: "PDF failed to load."
          }),
          /* @__PURE__ */ jsx("button", {
            onClick: onTryAgain,
            children: "Click to retry"
          })
        ]
      }) : /* @__PURE__ */ jsxs(PDFContentProvider, {
        pdf,
        tinyPagesVisible: tinyPagesIsVisible,
        userScrolled,
        setUserScrolled,
        currentPage,
        setCurrentPage,
        scale,
        positionY,
        contentRef,
        children: [
          (tinyPagesIsVisible || isFullScreen) && /* @__PURE__ */ jsx(TinyPagesList, {
            pageWidth: isFullScreen ? TINY_PAGE_WIDTH_FULL_SCREEN : TINY_PAGE_WIDTH,
            portalTo: isFullScreen ? void 0 : extraZoneRef ?? void 0,
            forcedVisible: !isFullScreen,
            isFullscreen: isFullScreen
          }),
          /* @__PURE__ */ jsxs(PDFInner, {
            ref: setInnerRef,
            children: [
              documentLoading && /* @__PURE__ */ jsx(DocumentLoader, {
                children: /* @__PURE__ */ jsx(Spinner, {
                  size: 30,
                  thickness: 3
                })
              }),
              /* @__PURE__ */ jsx(ScalableContainer, {
                children: /* @__PURE__ */ jsx(PdfDocument, {
                  fileUrl,
                  onLoadSuccess,
                  onLoadError,
                  hasSelection,
                  isMouseDown,
                  tinyPagesIsVisible,
                  isFullScreen,
                  width: contentRect?.width || 0,
                  wrapperRef: innerRef
                }, `${fileUrl}--${retryAttempts}`)
              })
            ]
          }),
          pdf && fileUrl && /* @__PURE__ */ jsx(HeaderControls, {
            pdf,
            isOwner: resource.user?.id === user?.id,
            fileName: fullName,
            storedFileUrl: fileUrl
          }),
          /* @__PURE__ */ jsx(PDFToolbar, {
            innerDivRef: contentRef.current
          })
        ]
      })
    })
  });
};

export { StoredPDFContent as default };
//# sourceMappingURL=StoredPDFContent.js.map
